import React, { useEffect, useState, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { initializeScene, drawTraj, drawTrajCalc, getAdjustedMotionList, engine, drawScene, resetScene } from './threed/traj';
import { GetCurrentSession } from '../isdlData/sessionData';
import { LMDrawingContext } from '../contexts/AppContext';
import { USER_PREFERENCES_IMPORTED_EVENT } from '../utils/userPreferences';

function ThreeDTraj({ selectedShot, customCanvasName = "canvas", key }) {
  const { lmDrawing } = useContext(LMDrawingContext);
  const [initialized, setInitialized] = useState(false);
  const [babylonModules, setBabylonModules] = useState(null);

  // Ref for the canvas element
  const canvasRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      const BABYLON = await import('babylonjs');
      const BABYLONMAT = await import('babylonjs-materials');
      const BABYLONGUI = await import('babylonjs-gui');
      
      if (isMounted) {
        setBabylonModules({ BABYLON, BABYLONMAT, BABYLONGUI });
      }
    })();

    return () => { 
      isMounted = false; 
      if (engine) {
        resetScene();
      }
    };
  }, []);

  useEffect(() => {
    if (engine) {
      engine.resize();
      drawScene(canvasRef.current);
    }
  }, [customCanvasName]);

  useEffect(() => {
    const handleImportedPreferences = () => {
      if (engine && canvasRef.current) {
        drawScene(canvasRef.current);
      }
    };

    window.addEventListener(USER_PREFERENCES_IMPORTED_EVENT, handleImportedPreferences);
    return () => {
      window.removeEventListener(USER_PREFERENCES_IMPORTED_EVENT, handleImportedPreferences);
    };
  }, []);

  // Initialize scene once modules and canvas are ready
  useEffect(() => {
    if (!initialized && babylonModules && canvasRef.current) {
      const { BABYLON, BABYLONMAT, BABYLONGUI } = babylonModules;
      initializeScene(BABYLON, BABYLONMAT, BABYLONGUI, canvasRef.current);
      setInitialized(true);

      if (engine) {
        engine.resize();
      }
    }
  }, [initialized, babylonModules, canvasRef.current, key]);

  // Update trajectories when selectedShot or lmDrawing changes
  useEffect(() => {
    if(initialized && !selectedShot){
      drawTraj(null);
    }
    
    if (!initialized || !selectedShot) {
      return;
    }

    let curveFit = null;
    const hasCurveFit = selectedShot.LMInfos?.some(item => {
      if (item.LMType === lmDrawing && item.CurveFit) {
        curveFit = JSON.parse(item.CurveFit);
        return true;
      }
      return false;
    });

    if (hasCurveFit) {
      const trajData = ParseCurveFit(curveFit);
      const adjTrajData = getAdjustedMotionList(trajData, GetAngleFromSession(selectedShot));
      drawTraj(adjTrajData);
    } else {
      drawTraj(null);
    }

    if (engine) {
      engine.resize();
    }

  }, [initialized, selectedShot, lmDrawing, customCanvasName]);

  function ParseCurveFit(data) {
    if (!data) return null;
    const xfit = data.XFit;
    const yfit = data.YFit;
    const zfit = data.ZFit;
    const timeInt = data.TimeInterval;

    const evaluatePolynomial = (coeffs, t) =>
      coeffs.reduce((sum, coeff, i) => sum + coeff * Math.pow(t, i), 0);

    const numPoints = 100;
    const timeStep = (timeInt[1] - timeInt[0]) / (numPoints - 1);
    const timePoints = Array.from({ length: numPoints }, (_, i) => timeInt[0] + i * timeStep);

    return timePoints.map(t => [
      evaluatePolynomial(xfit, t),
      evaluatePolynomial(yfit, t),
      evaluatePolynomial(zfit, t),
    ]);
  }

  function GetAngleFromSession(shot) {
    const currentSession = GetCurrentSession();
    const bayIndex = shot.BayIndex;
    const targetIndex = shot.TargetIndex;

    if (!currentSession.Bays || currentSession.Bays.length <= bayIndex || bayIndex=== undefined) return 0.0;
    if (!currentSession.Targets || currentSession.Targets.length <= targetIndex || targetIndex=== undefined) return 0.0;

    if(bayIndex < 0){
      return 0.0;
    }

    if(targetIndex < 0){
      return 0.0;
    }

    const bayPos = [
      currentSession.Bays[bayIndex].X,
      currentSession.Bays[bayIndex].Y,
      currentSession.Bays[bayIndex].Z,
    ];
    const targetPos = [
      currentSession.Targets[targetIndex].X,
      currentSession.Targets[targetIndex].Y,
      currentSession.Targets[targetIndex].Z,
    ];

    const forwardDir = [0, 0, 1];
    const upDir = [0, 1, 0];
    const targetDir = normalize(subtractVectors(targetPos, bayPos));
    const angle = signedAngle(forwardDir, targetDir, upDir);

    function normalize(v) {
      const len = Math.sqrt(v[0]*v[0] + v[1]*v[1] + v[2]*v[2]);
      return [v[0]/len, v[1]/len, v[2]/len];
    }

    function subtractVectors(a, b) {
      return [a[0]-b[0], a[1]-b[1], a[2]-b[2]];
    }

    function signedAngle(a, b, up) {
      const dot = a[0]*b[0] + a[1]*b[1] + a[2]*b[2];
      const det = a[0]*(b[1]*up[2] - b[2]*up[1])
                - a[1]*(b[0]*up[2] - b[2]*up[0])
                + a[2]*(b[0]*up[1] - b[1]*up[0]);
      return Math.atan2(det, dot)*(180/Math.PI);
    }

    return angle;
  }

  // If modules aren't loaded yet, just return null (or a loader)
  if (!babylonModules) return null;

  const container = document.getElementById(customCanvasName);
  if (!container) return null;

  // Use React Portal to place the canvas in the correct container
  return ReactDOM.createPortal(
    <canvas 
      name={customCanvasName}
      ref={canvasRef}
      id="threedcanvas"
      style={{ width: '100%', height: '100%', background:'brown' }}
    />,
    container
  );
}

export default ThreeDTraj;
